export const MIN_WORD_LEN = 3;
const VOWELS = "AEIOU";

// A rough English letter-frequency weighting for which letter comes up
// next — not Scrabble's specific 100-tile bag or point values, just a
// plausible distribution so vowels and common consonants show up more
// than Q/Z/X/J. Used by Classic and Blitz.
export const LETTER_WEIGHTS = {
  E: 12, A: 9, I: 9, O: 8, N: 7, R: 7, T: 7, S: 6, L: 4, U: 4,
  D: 4, G: 3, C: 3, M: 3, B: 2, P: 2, F: 2, H: 2, W: 2, Y: 2,
  V: 1, K: 1, J: 1, X: 1, Q: 1, Z: 1,
};

// Challenge requires the *entire* row/column run to be a real word — no
// partial credit for a valid substring — which is meaningfully harder to
// land than Classic/Blitz's "longest valid word within the run" rule.
// A few more vowels in the bag makes full-run words easier to complete.
// +1 to each vowel's weight moves vowel share from 42/102 (41.2%) to
// 47/107 (43.9%) — about 2-3 points more, matching "~2% more vowels."
export const CHALLENGE_LETTER_WEIGHTS = Object.fromEntries(
  Object.entries(LETTER_WEIGHTS).map(([letter, weight]) => [
    letter,
    VOWELS.includes(letter) ? weight + 1 : weight,
  ])
);

const poolCache = new WeakMap();

function poolFor(weights) {
  let pool = poolCache.get(weights);
  if (!pool) {
    pool = Object.entries(weights).flatMap(([letter, weight]) => Array(weight).fill(letter));
    poolCache.set(weights, pool);
  }
  return pool;
}

export function pickRandomLetter(weights = LETTER_WEIGHTS, rng = Math.random) {
  const pool = poolFor(weights);
  return pool[Math.floor(rng() * pool.length)];
}

// Game modes. `matchStrategy`:
//  - "substring": a drop clears the longest valid word found within its
//    row/column run that contains the dropped tile (Classic, Blitz).
//  - "exact": a drop only clears when the *entire* contiguous run — the
//    whole row or column segment, not a piece of it — is itself a
//    valid word (Challenge).
// `cols`/`rows` size the board; the board's on-page footprint (its CSS
// max-width) stays the same across modes, so fewer columns/rows reads
// as bigger tiles, and a smaller total cell count means it tops out
// sooner — that's Blitz. `letterWeights` picks which bag pickRandomLetter
// draws from. `persistsScore` gates the Best-score/leaderboard UI and the
// getStacksScore/submitStacksScore/getStacksLeaderboard calls in Stacks.js
// — true for all three here since the backend (StacksScore model,
// StacksScoreView/StacksLeaderboard) tracks exactly these three modes;
// Drill sets it false in drill.js since its config differs every session.
export const MODES = {
  classic: {
    id: "classic",
    name: "Classic",
    tagline: "The original board, full size.",
    cols: 8,
    rows: 10,
    matchStrategy: "substring",
    letterWeights: LETTER_WEIGHTS,
    persistsScore: true,
  },
  blitz: {
    id: "blitz",
    name: "Blitz",
    tagline: "Smaller board, bigger tiles, over quick.",
    cols: 6,
    rows: 6,
    matchStrategy: "substring",
    letterWeights: LETTER_WEIGHTS,
    persistsScore: true,
  },
  challenge: {
    id: "challenge",
    name: "Challenge",
    tagline: "No partial credit: the whole row or column must be a word.",
    cols: 8,
    rows: 10,
    matchStrategy: "exact",
    letterWeights: CHALLENGE_LETTER_WEIGHTS,
    persistsScore: true,
  },
};

export const DEFAULT_MODE = "classic";

export function getMode(id) {
  return MODES[id] || MODES[DEFAULT_MODE];
}
